<div class="relative min-h-screen bg-slate-50 -m-4 sm:-m-8">

  @if (data()?.loading) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-slate-50/70 backdrop-blur-[1px]"
         role="status" aria-live="polite" aria-busy="true">
      <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-8 py-6 shadow-lg">
        <i class="pi pi-spin pi-spinner text-3xl text-rose-600"></i>
        <div class="text-center">
          <p class="text-sm font-semibold text-slate-800">Cargando churn / retención…</p>
          <p class="mt-0.5 text-xs text-slate-500">Analizando suscripciones del periodo</p>
        </div>
      </div>
    </div>
  }

  <!-- Header -->
  <div class="bg-white border-b border-slate-200 px-6 py-5">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div class="flex items-center gap-2">
          <span class="flex items-center justify-center w-9 h-9 rounded-xl bg-rose-600">
            <i class="pi pi-users text-white text-base"></i>
          </span>
          <div>
            <h1 class="text-2xl font-bold text-slate-900">Forecast de churn / retención</h1>
            <p class="text-xs text-slate-500">Salud de suscripciones · MRR en riesgo</p>
          </div>
        </div>
        <p class="mt-1 text-sm text-slate-500 ml-11 capitalize">{{ todayLabel() }}</p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <span class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
          <i class="pi pi-calendar text-slate-400"></i>
          {{ periodLabel() }}
        </span>

        <div class="flex rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm"
             [class.pointer-events-none]="data()?.loading"
             [class.opacity-60]="data()?.loading">
          @for (opt of horizonOptions; track opt.value) {
            <button type="button" (click)="setHorizon(opt.value)"
              [disabled]="data()?.loading"
              class="px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-wait"
              [class.bg-rose-600]="period().horizon === opt.value && opt.value !== 'custom'"
              [class.text-white]="period().horizon === opt.value && opt.value !== 'custom'"
              [class.text-slate-600]="period().horizon !== opt.value"
              [class.hover:bg-slate-50]="period().horizon !== opt.value && !data()?.loading">
              {{ opt.label }}
            </button>
          }
        </div>

        <button type="button" (click)="load()" [disabled]="data()?.loading"
          class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-xs font-medium disabled:opacity-50 shadow-sm">
          <i class="pi pi-refresh text-sm" [class.animate-spin]="data()?.loading"></i>
          Actualizar
        </button>
      </div>
    </div>

    @if (period().horizon === 'custom') {
      <div class="mt-4 flex flex-wrap items-center gap-3 ml-11">
        <input type="date" [value]="customDateFrom()"
          (input)="customDateFrom.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500">
        <span class="text-slate-400 text-sm">—</span>
        <input type="date" [value]="customDateTo()"
          (input)="customDateTo.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500">
        <button type="button" (click)="applyCustomPeriod()"
          [disabled]="!customDateFrom() || !customDateTo() || !!data()?.loading"
          class="px-4 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50">
          Aplicar
        </button>
      </div>
    }
  </div>

  <div class="bg-rose-800 text-white px-6 py-3 text-sm">
    <div class="flex flex-wrap items-center gap-x-8 gap-y-2">
      <span class="flex items-start gap-2 max-w-3xl">
        <i class="pi pi-info-circle mt-0.5 opacity-80"></i>
        <span>
          Mide la <strong>salud de tu cartera recurrente</strong>:
          cuánto MRR está sano, cuánto está atrasado y cuánto se perdió por cancelaciones o fin de suscripción en el periodo.
        </span>
      </span>
      @if (data()?.kpis; as k) {
        <span class="flex items-center gap-2.5 ml-auto shrink-0">
          <span class="opacity-90 inline-flex items-center gap-1.5 text-xs">
            MRR en riesgo
            <i class="pi pi-question-circle text-rose-100/90 cursor-help text-xs"
              [pTooltip]="help.atRisk"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </span>
          <strong class="text-xl sm:text-2xl font-bold tabular-nums text-white leading-none">
            {{ k.atRiskMrr | currency:'MXN':'symbol-narrow':'1.0-0' }}
          </strong>
          <span class="text-xs text-amber-200 tabular-nums opacity-90">
            {{ k.atRiskRate | number:'1.0-1' }}%
          </span>
        </span>
      }
    </div>
  </div>

  @if (data()?.error) {
    <div class="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-amber-500"></i>
      {{ data()?.error }}
    </div>
  }

  <div class="p-6 space-y-5">

    <!-- KPIs -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            MRR sano
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.healthy" tooltipPosition="bottom" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <i class="pi pi-heart text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.healthyMrr ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ data()?.kpis?.healthyCount ?? 0 }} suscripciones activas/prueba
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            MRR en riesgo
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.atRisk" tooltipPosition="bottom" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <i class="pi pi-exclamation-triangle text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.atRiskMrr ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ data()?.kpis?.atRiskCount ?? 0 }} atrasadas (PAST_DUE)
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            MRR churned
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.churned" tooltipPosition="bottom" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
            <i class="pi pi-minus-circle text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.churnedMrr ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ data()?.kpis?.churnedCount ?? 0 }} bajas en el periodo
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Retención
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem]"
              [pTooltip]="help.retention" tooltipPosition="left" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
            <i class="pi pi-percentage text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.revenueRetentionRate ?? 100) | number:'1.0-1' }}%
        </p>
        <p class="mt-1 text-xs text-slate-500">
          Logos {{ (data()?.kpis?.logoRetentionRate ?? 100) | number:'1.0-1' }}% ·
          ingresos {{ (data()?.kpis?.revenueRetentionRate ?? 100) | number:'1.0-1' }}%
        </p>
      </div>
    </div>

    <!-- Charts -->
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div class="xl:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="mb-3">
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Churn y riesgo en el tiempo
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
              [pTooltip]="help.timeline" tooltipPosition="right" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500">Activas vs en riesgo vs churned (MRR por mes)</p>
        </div>
        <div class="h-72">
          <p-chart type="bar" [data]="timelineChart()" [options]="timelineOptions" styleClass="h-full w-full" />
        </div>
      </div>

      <div class="xl:col-span-4 space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 overflow-hidden">
          <h2 class="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5">
            Cartera por estado
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
              [pTooltip]="help.status" tooltipPosition="left" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500 mb-3">Distribución de MRR</p>
          <div class="flex items-center gap-3 min-h-0">
            <div class="relative shrink-0 w-28 h-28 overflow-hidden">
              <p-chart type="doughnut" [data]="statusChart()" [options]="doughnutOptions" class="block h-full w-full" />
            </div>
            <ul class="min-w-0 flex-1 space-y-2 max-h-32 overflow-y-auto">
              @for (seg of statusLegend(); track seg.label) {
                <li class="flex items-center gap-2 text-xs">
                  <span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background]="seg.color"></span>
                  <span class="text-slate-600 truncate">{{ seg.label }}</span>
                  <span class="font-semibold text-slate-900 ml-auto tabular-nums pl-2">
                    {{ seg.count }}
                  </span>
                </li>
              }
            </ul>
          </div>
        </div>

        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100">
            <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
              Riesgo / churn por frecuencia
              <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
                [pTooltip]="help.frequency" tooltipPosition="left" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
            </h2>
          </div>
          <ul class="divide-y divide-slate-100 max-h-40 overflow-y-auto">
            @for (row of data()?.byFrequency ?? []; track row.frequency) {
              <li class="px-4 py-2.5 flex items-center justify-between gap-3">
                <div>
                  <p class="text-sm font-medium text-slate-800">{{ frequencyLabel(row.frequency) }}</p>
                  <p class="text-xs text-slate-400">{{ row.count }} suscripciones</p>
                </div>
                <p class="text-sm font-semibold tabular-nums text-slate-900">
                  {{ row.mrr | currency:'MXN':'symbol-narrow':'1.0-0' }}
                </p>
              </li>
            } @empty {
              <li class="px-4 py-6 text-center text-sm text-slate-400">Sin datos</li>
            }
          </ul>
        </div>
      </div>
    </div>

    <!-- Tables -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div class="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            @if (tableTab() === 'risk') {
              Suscripciones en riesgo
              <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
                [pTooltip]="help.atRiskTable" tooltipPosition="right" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
            } @else if (tableTab() === 'churned') {
              Bajas del periodo
              <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
                [pTooltip]="help.churnedTable" tooltipPosition="right" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
            } @else {
              Renovaciones próximos 30 días
              <i class="pi pi-question-circle text-slate-400 cursor-help text-xs"
                [pTooltip]="help.renewals" tooltipPosition="right" tooltipStyleClass="forecast-help-tooltip" [showDelay]="200"></i>
            }
          </h2>
          <p class="text-xs text-slate-500">Prioriza recuperación y seguimiento</p>
        </div>
        <div class="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
          <button type="button" (click)="setTableTab('risk')"
            class="px-3 py-1.5"
            [class.bg-amber-600]="tableTab() === 'risk'"
            [class.text-white]="tableTab() === 'risk'"
            [class.text-slate-600]="tableTab() !== 'risk'">
            En riesgo ({{ data()?.atRisk?.length ?? 0 }})
          </button>
          <button type="button" (click)="setTableTab('churned')"
            class="px-3 py-1.5 border-l border-slate-200"
            [class.bg-rose-600]="tableTab() === 'churned'"
            [class.text-white]="tableTab() === 'churned'"
            [class.text-slate-600]="tableTab() !== 'churned'">
            Bajas ({{ data()?.churned?.length ?? 0 }})
          </button>
          <button type="button" (click)="setTableTab('renewals')"
            class="px-3 py-1.5 border-l border-slate-200"
            [class.bg-sky-600]="tableTab() === 'renewals'"
            [class.text-white]="tableTab() === 'renewals'"
            [class.text-slate-600]="tableTab() !== 'renewals'">
            Renovaciones ({{ data()?.upcomingRenewals?.length ?? 0 }})
          </button>
        </div>
      </div>

      <p-table
        [value]="tableRows()"
        [paginator]="true"
        [rows]="15"
        [rowsPerPageOptions]="[15, 30, 50]"
        styleClass="p-datatable-sm"
        [sortField]="'mrr'"
        [sortOrder]="-1">
        <ng-template pTemplate="header">
          <tr>
            <th class="text-left">Cliente</th>
            <th class="text-left hidden md:table-cell">Plan</th>
            <th class="text-left">Estado</th>
            <th class="text-left hidden sm:table-cell">Riesgo</th>
            <th class="text-left hidden lg:table-cell">Frecuencia</th>
            <th class="text-center hidden lg:table-cell">
              {{ tableTab() === 'churned' ? 'Salida' : 'Próx. cobro' }}
            </th>
            <th class="text-right" pSortableColumn="mrr">MRR <p-sortIcon field="mrr" /></th>
            <th class="text-left hidden xl:table-cell">Motivo</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td class="text-left">
              <div class="flex items-center gap-2 min-w-0">
                <i class="pi pi-user text-rose-500 text-xs"></i>
                <a [routerLink]="['/subscriptions', row.id]"
                  class="text-sm text-slate-800 hover:text-rose-700 truncate font-medium">
                  {{ row.customerName }}
                </a>
              </div>
              @if (row.number) {
                <p class="text-xs text-slate-400 ml-5">{{ row.number }}</p>
              }
            </td>
            <td class="text-left hidden md:table-cell text-sm text-slate-600 truncate max-w-[10rem]">
              {{ row.planName }}
            </td>
            <td class="text-left">
              <p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)" />
            </td>
            <td class="text-left hidden sm:table-cell">
              <p-tag [value]="riskLabel(row.risk)" [severity]="riskSeverity(row.risk)" />
            </td>
            <td class="text-left hidden lg:table-cell text-sm text-slate-600">
              {{ frequencyLabel(row.frequency) }}
            </td>
            <td class="text-center hidden lg:table-cell tabular-nums text-sm">
              {{ formatDay(tableTab() === 'churned' ? row.endDate : row.nextBillingDate) }}
            </td>
            <td class="text-right tabular-nums font-semibold">
              {{ row.mrr | currency:'MXN':'symbol-narrow':'1.0-0' }}
            </td>
            <td class="text-left hidden xl:table-cell text-xs text-slate-500">
              {{ row.reason }}
              @if (row.collectionMethod) {
                <span class="text-slate-400"> · {{ methodLabel(row.collectionMethod) }}</span>
              }
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="8" class="text-center text-slate-400 py-10 text-sm">
              @if (tableTab() === 'risk') {
                No hay suscripciones atrasadas
              } @else if (tableTab() === 'churned') {
                No hubo bajas en el periodo
              } @else {
                No hay renovaciones en los próximos 30 días
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- Glosario -->
    <footer class="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button type="button"
        class="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        (click)="toggleGlossary()"
        [attr.aria-expanded]="glossaryOpen()">
        <div>
          <h2 class="text-sm font-semibold text-slate-900">Términos usados</h2>
          <p class="mt-0.5 text-xs text-slate-500">
            Acrónimos y definiciones cortas de las métricas que aparecen en esta pantalla.
          </p>
        </div>
        <i class="pi text-slate-400 text-sm shrink-0"
          [class.pi-chevron-down]="!glossaryOpen()"
          [class.pi-chevron-up]="glossaryOpen()"></i>
      </button>
      @if (glossaryOpen()) {
        <dl class="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border-t border-slate-100 pt-4">
          @for (item of glossary; track item.term) {
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-rose-700">{{ item.term }}</dt>
              <dd class="mt-1 text-sm leading-relaxed text-slate-600">{{ item.definition }}</dd>
            </div>
          }
        </dl>
      }
    </footer>

  </div>
</div>
